import { createFileRoute, Link, Outlet } from '@tanstack/react-router';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import BookmarkList from '../components/BookmarkList';
import LayoutSelector from '../components/layouts/LayoutSelector';
import Avatar from '../components/ui/Avatar';
import Icon from '../components/ui/Icon';
import NotFoundPage from '../components/NotFoundPage';
import PublicFoldersMenu from '../components/Profile/PublicFoldersMenu';
import type { LayoutConfig } from '../types/layout';
import { APIError } from '../services/apiClient';
import { useUserProfile } from '../services/dataHooks';
import { useCurrentUser } from '../services/userHooks';
import { useMobileNavSidebar } from '../hooks/useUserPreferences';

export const Route = createFileRoute('/p/$id')({
  component: UserProfileRoute,
});

// Types and fetch logic moved to types/api and services/dataHooks

interface UserProfileContentProps {
  userId: string;
  page: number;
  folderId?: string;
  folderSlug?: string;
}

export function UserProfileContent({
  userId,
  page,
  folderId,
  folderSlug,
}: UserProfileContentProps) {
  useLingui();
  const { currentUser } = useCurrentUser();
  const { toggleMobileNavSidebar } = useMobileNavSidebar();
  const {
    profile: data,
    isLoading: isPending,
    error,
  } = useUserProfile(userId, page, folderId);

  const layoutConfig: LayoutConfig = {
    type: 'hybrid',
    showAuthButtons: true,
  };

  const publicFolders = data?.public_folders ?? [];
  const displayName = data?.user.display_name || data?.user.username;
  const headerTitle = displayName;
  // Only the owner reaches their own private profile (the API 404s everyone
  // else), so let them know the page isn't publicly visible. Public folder
  // pages stay reachable regardless of the profile's privacy, so the notice
  // (which claims "only you can view this page") doesn't apply there.
  const showPrivateNotice = Boolean(
    data?.is_own_profile && data?.user.is_private && !data?.folder,
  );

  const inFolder = Boolean(folderId && folderSlug);
  const hasPagination = page > 1 || data?.has_next;
  const showNewerArticles = page > 1;
  const showOlderArticles = data?.has_next;

  // Legacy-style public URLs: /p/<id>/folder/<folderId>/<slug>[/<page>] for a
  // public folder, /p/<id>[/<page>] for the liked list.
  const pageLink = (targetPage: number) => {
    if (inFolder) {
      return targetPage <= 1
        ? {
            to: '/p/$id/folder/$folderId/$slug' as const,
            params: { id: userId, folderId: folderId!, slug: folderSlug! },
          }
        : {
            to: '/p/$id/folder/$folderId/$slug/$page' as const,
            params: {
              id: userId,
              folderId: folderId!,
              slug: folderSlug!,
              page: `${targetPage}`,
            },
          };
    }
    return targetPage <= 1
      ? { to: '/p/$id' as const, params: { id: userId } }
      : { to: '/p/$id/$page' as const, params: { id: userId, page: `${targetPage}` } };
  };

  const newerHref = showNewerArticles ? pageLink(page - 1) : null;
  const olderHref = showOlderArticles ? pageLink(page + 1) : null;

  const avatarProps = {
    avatarUrl: data?.user.profile_picture_url,
    displayName: data?.user.display_name,
    username: data?.user.username,
    alt: displayName || t`Profile`,
  };

  // A 404 means the user doesn't exist or the profile/folder is private — show
  // the app's 404 page rather than a generic error.
  if (error instanceof APIError && error.status === 404) {
    return <NotFoundPage />;
  }

  return (
    <LayoutSelector config={layoutConfig} title={headerTitle || t`Not Found`}>
      {error ? (
        <div className="flex min-h-[50vh] flex-col items-center justify-center text-gray-600">
          <p>
            <Trans>Sorry, there was an error. Please try again.</Trans>
          </p>
        </div>
      ) : (
        <>
          <div className="z-high sticky top-0 bg-white">
            <div className="flex items-center px-3 md:px-4 md:py-3">
              {currentUser ? (
                <>
                  <button
                    type="button"
                    className="my-2 shrink-0 md:hidden"
                    title={t`Menu`}
                    aria-label={t`Menu`}
                    onClick={toggleMobileNavSidebar}
                  >
                    <Avatar {...avatarProps} className="h-8 w-8" />
                  </button>
                  <Avatar
                    {...avatarProps}
                    className="hidden h-10 w-10 shrink-0 md:block"
                  />
                </>
              ) : (
                <Avatar
                  {...avatarProps}
                  className="my-2 h-8 w-8 shrink-0 md:my-0 md:h-10 md:w-10"
                />
              )}
              <div className="flex-fluid ml-2">
                <h1 className="text-sm font-semibold md:text-lg">
                  {headerTitle || t`Loading...`}
                </h1>
                {publicFolders.length > 0 && (
                  <PublicFoldersMenu
                    userId={userId}
                    folders={publicFolders}
                    selectedFolderId={folderId}
                  />
                )}
              </div>
              {!currentUser && (
                <a
                  href="/user/login"
                  className="text-copy-secondary shrink-0 px-3 text-sm md:hidden"
                >
                  <Trans>Sign In</Trans>
                </a>
              )}
            </div>
          </div>

          {showPrivateNotice && (
            <div
              role="status"
              className="border-active bg-surface-secondary text-copy-secondary mx-4 mt-4 rounded-md border-l-4 px-4 py-3 text-sm"
            >
              <Trans>
                Your profile is currently set to private. Only you can view this page.
              </Trans>
            </div>
          )}

          <BookmarkList
            bookmarks={data?.bookmarks || []}
            isLoading={isPending}
            mode="liked"
            emptyMessage={data?.folder ? t`No articles in this folder.` : undefined}
            header={
              data?.folder ? (
                <div className="text-copy-primary flex items-center gap-2 px-5 py-4 font-semibold">
                  <Icon name="folder-public" className="relative -top-px h-6 w-6" />
                  <span>{data.folder.title}</span>
                </div>
              ) : undefined
            }
            profileMode={true}
            showThumbnails={true}
          />

          {hasPagination && (
            <div className="bookmark-list-pagination text-copy-secondary flex justify-between border-t border-gray-300 py-6 text-sm">
              {showNewerArticles ? (
                newerHref && (
                  <Link {...newerHref} className="px-4 py-2">
                    ← <Trans>Newer Articles</Trans>
                  </Link>
                )
              ) : (
                <div />
              )}

              {showOlderArticles && olderHref && (
                <Link {...olderHref} className="px-4 py-2">
                  <Trans>Older Articles</Trans> →
                </Link>
              )}
            </div>
          )}
        </>
      )}
    </LayoutSelector>
  );
}

function UserProfileRoute() {
  return <Outlet />;
}
